// 週期的「今天」與「哪一筆經期」—— **純函式**（W58.5）。
//
// **為什麼是純函式而不是元件。** `CycleView.tsx` 已經接近 30 KB，而這三件事要
// 被**三個地方**用：週期頁的主按鈕（今天在不在經期）、週期頁的歷史清單
// （今天覆蓋的是哪一筆）、以及日詳情的貼士（今天是不是經期前夕）。
//
// 把它們放進一個 `.ts`（不是 `.tsx`）是因為 **Vite 的 fast refresh 要求
// 「一個檔案只匯出元件」** —— 這三個函式與元件住在一起就會印警告。
// （`cycleState.ts` 是同一個道理。）

import type { CalendarCycle, CycleHistoryEntry } from "@/lib/api/calendar";

/**
 * Today on the **client's** calendar day.
 *
 * **為什麼不用伺服器的「今天」。** 伺服器有 `today_hkt()`（ADR-0123），而
 * 週期頁的 payload 裡沒有它 —— 只有相對於它的兩個差值
 * （`days_until_next` / `days_late`）。而「經期來了」這個按鈕要開的是
 * **建立事件的表單**，那個表單要的是一個日期；這個日期用哪裡的「今天」，
 * 決定了表單預設落在哪一天。
 *
 * 所以：**要開表單** → 用客戶端日曆日（與月曆頁的「+ 新增」同一個來源）；
 * **要判斷狀態** → 用 `serverToday()`（推導出來的，與倒數同一個數字）。
 */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * The server's "today", derived rather than read from the clock.
 *
 * `days_until_next` is `predicted_start - server_today`, so the server's today
 * is `predicted_start - days_until_next`. Using that instead of the device
 * clock removes the client/server skew entirely — and it is the same number
 * the countdown itself is built from, so the progress bar and the countdown
 * can never disagree.
 */
export function serverToday(cycle: CalendarCycle): string | null {
  const projection = cycle.projection;
  if (!projection || !cycle.predicted_start || projection.days_until_next == null) {
    return null;
  }
  const base = Date.parse(`${cycle.predicted_start}T00:00:00Z`);
  return new Date(base - projection.days_until_next * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/**
 * The recorded period that covers `day`, if any.
 *
 * **「實際經期」是事實，所以它來自歷史記錄，不是來自推算。** 一筆經期事件
 * 覆蓋的那幾天就是實際經期 —— 不需要（也不該）用預測去猜。
 */
export function periodEventOn(
  history: readonly CycleHistoryEntry[],
  day: string,
): CycleHistoryEntry | null {
  for (const entry of history) {
    const end = entry.end_date ?? entry.start_date;
    if (entry.start_date <= day && day <= end) return entry;
  }
  return null;
}

/**
 * Is `day` inside a recorded period?
 *
 * **與「預測經期」不同。** 預測經期是猜的，而這是事實 —— 關掉預測也不會
 * 讓它消失（ADR-0211 的 `actual` 通道）。
 */
export function isActualPeriodDay(
  history: readonly CycleHistoryEntry[],
  day: string,
): boolean {
  return periodEventOn(history, day) !== null;
}

/**
 * Is `day` within two days before a period starts?
 *
 * **「經期前夕」是使用者指定要貼士的三個時段之一。**
 *
 * **兩個起點都算：**
 *
 * - `last_start` —— **最近一筆經期記錄的開始日**。如果它比 `day` 還晚，
 *   那是一筆**已經被記下來**的經期（可能是今天才補記的明天的經期），
 *   所以那個前夕是**事實**。
 * - `predicted_start` —— 推算的下一次經期開始日。這個是**推算**，但它是
 *   日詳情唯一看得到的「未來」。
 *
 * **兩個都要看。** 只看 `predicted_start` 的話，一個「明天就開始」的經期
 * （已經記錄了）會被當成推算；而只看 `last_start` 的話，完全沒有記錄的
 * 配對永遠不會有前夕提醒。
 */
export function isPeriodEve(
  inference: { last_start: string; predicted_start: string } | null,
  day: string,
): boolean {
  if (!inference) return false;
  const base = Date.parse(`${day}T00:00:00Z`);
  return [inference.last_start, inference.predicted_start].some((target) => {
    const diff = Math.round((Date.parse(`${target}T00:00:00Z`) - base) / 86_400_000);
    return diff >= 0 && diff <= 2;
  });
}
